import './client-details.scss'

import styled from '@emotion/styled'
import { useRef } from 'react'

import {
  ApiClientDefaultCashInstruments,
  ApiClientDefaultFees,
  ApiClientTradeBuildingSettings,
  ApiClientType,
  ApiFee,
  ApiFeeBasis,
  ApiFeeDayCount,
  ApiFeeDeductionFrequency,
  ApiFeeType,
  ApiNewBusinessUnit,
  ApiReinvestmentType
} from '@/api/_legacy/generated-api'
import {
  FeesInput,
  Label,
  RadioInput,
  Section,
  Select,
  Space,
  Strategy,
  StrategySelector,
  TextInput,
  Typography,
  ValidationMessage
} from '@/components/core-v2'

import { filterFeeTypes, isErrorWith, unpascal } from '../common'
import { getIsChecked, setStrategies } from '../common/helpers'
import { Mutation } from '../types'

type Client = {
  businessUnits: ApiNewBusinessUnit[]
  clientName: string
  clientType: ApiClientType
  defaultCashInstruments: ApiClientDefaultCashInstruments
  defaultFees?: ApiClientDefaultFees
  disallowedStrategies?: string[]
  reinvestmentType: ApiReinvestmentType
  tradeBuildingSettings: ApiClientTradeBuildingSettings
}

type Fee = {
  counterPartyId: string
  feeBasis: ApiFeeBasis
  feeDayCount: ApiFeeDayCount
  feeDeductionFrequency: ApiFeeDeductionFrequency
  feeId: string
  feeRate: number
  feeType: ApiFeeType
  isVATApplicable: boolean
}

type StrategyType = {
  id: string
  name: string
}

export type Props = {
  availableCptyIds: string[]
  availableDepositInstruments: string[]
  availableFeeTypes: string[]
  availableLoanInstruments: string[]
  client: Client
  clientName: string
  errors?: { [key: string]: string }
  existingClient: boolean
  newCPLeverageFee: Fee
  newContProtFee1: Fee
  newContProtFee2: Fee
  newContProtFee3: Fee
  newContProtFee4: Fee
  newDelta1Fee: Fee
  newNoteFee: Fee
  newRiskManagedFee: Fee
  newTermProtFee1: Fee
  newTermProtFee2: Fee
  newTermProtFee3: Fee
  newTermProtFee4: Fee
  newVCLeverageFee: Fee
  onMutate: (mutation: Mutation<Client>) => void
  onMutateCPLevFee: (mutation: Mutation<ApiFee>) => void
  onMutateContProtFee1: (mutation: Mutation<ApiFee>) => void
  onMutateContProtFee2: (mutation: Mutation<ApiFee>) => void
  onMutateContProtFee3: (mutation: Mutation<ApiFee>) => void
  onMutateContProtFee4: (mutation: Mutation<ApiFee>) => void
  onMutateDelta1Fee: (mutation: Mutation<ApiFee>) => void
  onMutateNoteFee: (mutation: Mutation<ApiFee>) => void
  onMutateRiskManagedFee: (mutation: Mutation<ApiFee>) => void
  onMutateTermProtFee1: (mutation: Mutation<ApiFee>) => void
  onMutateTermProtFee2: (mutation: Mutation<ApiFee>) => void
  onMutateTermProtFee3: (mutation: Mutation<ApiFee>) => void
  onMutateTermProtFee4: (mutation: Mutation<ApiFee>) => void
  onMutateVCLevFee: (mutation: Mutation<ApiFee>) => void
  strategies: StrategyType[]
}

type StrategySelected = 'yes' | 'no' | 'unavailable'

function getState(strategyTemplateId: string, client: Client): StrategySelected {
  return getIsChecked(strategyTemplateId, client.disallowedStrategies) ? 'yes' : 'no'
}

const StyledNewClientBlock = styled(Space)`
  overflow-y: scroll;
`

export function ClientEditor({
  availableCptyIds,
  availableDepositInstruments,
  availableFeeTypes,
  availableLoanInstruments,
  client,
  clientName,
  errors,
  existingClient,
  newCPLeverageFee,
  newContProtFee1,
  newContProtFee2,
  newContProtFee3,
  newContProtFee4,
  newDelta1Fee,
  newNoteFee,
  newRiskManagedFee,
  newTermProtFee1,
  newTermProtFee2,
  newTermProtFee3,
  newTermProtFee4,
  newVCLeverageFee,
  onMutate,
  onMutateCPLevFee,
  onMutateContProtFee1,
  onMutateContProtFee2,
  onMutateContProtFee3,
  onMutateContProtFee4,
  onMutateDelta1Fee,
  onMutateNoteFee,
  onMutateRiskManagedFee,
  onMutateTermProtFee1,
  onMutateTermProtFee2,
  onMutateTermProtFee3,
  onMutateTermProtFee4,
  onMutateVCLevFee,
  strategies = []
}: Props) {
  const strategyPermissions: Strategy[] = strategies.map(x => ({
    ...x,
    selected: getState(x.id, client)
  }))

  const availableFeeBases = ['TotalTradeValue', 'RiskyAssetExposure', 'LoanAmount']
  const availableFeeFreqs = ['Quarterly', 'Monthly', 'Annually']

  const strategyIds = strategies.map(x => x.id)

  const newFeeMap = {
    Delta1: {
      false: {
        false: newDelta1Fee,
        true: newDelta1Fee
      },
      true: {
        false: newDelta1Fee,
        true: newDelta1Fee
      }
    },
    RiskManaged: {
      false: {
        false: newRiskManagedFee,
        true: newRiskManagedFee
      },
      true: {
        false: newRiskManagedFee,
        true: newRiskManagedFee
      }
    },
    ConstantProportionLeverage: {
      false: {
        false: newCPLeverageFee,
        true: newCPLeverageFee
      },
      true: {
        false: newCPLeverageFee,
        true: newCPLeverageFee
      }
    },
    VolatilityControlLeverage: {
      false: {
        false: newVCLeverageFee,
        true: newVCLeverageFee
      },
      true: {
        false: newVCLeverageFee,
        true: newVCLeverageFee
      }
    },
    TermProtection: {
      false: {
        false: newTermProtFee1,
        true: newTermProtFee2
      },
      true: {
        false: newTermProtFee3,
        true: newTermProtFee4
      }
    },
    ContinuousProtection: {
      false: {
        false: newContProtFee1,
        true: newContProtFee2
      },
      true: {
        false: newContProtFee3,
        true: newContProtFee4
      }
    }
  }

  const mutationMap = {
    Delta1: {
      false: {
        false: onMutateDelta1Fee,
        true: onMutateDelta1Fee
      },
      true: {
        false: onMutateDelta1Fee,
        true: onMutateDelta1Fee
      }
    },
    RiskManaged: {
      false: {
        false: onMutateRiskManagedFee,
        true: onMutateRiskManagedFee
      },
      true: {
        false: onMutateRiskManagedFee,
        true: onMutateRiskManagedFee
      }
    },
    ConstantProportionLeverage: {
      false: {
        false: onMutateCPLevFee,
        true: onMutateCPLevFee
      },
      true: {
        false: onMutateCPLevFee,
        true: onMutateCPLevFee
      }
    },
    VolatilityControlLeverage: {
      false: {
        false: onMutateVCLevFee,
        true: onMutateVCLevFee
      },
      true: {
        false: onMutateVCLevFee,
        true: onMutateVCLevFee
      }
    },
    TermProtection: {
      false: {
        false: onMutateTermProtFee1,
        true: onMutateTermProtFee2
      },
      true: {
        false: onMutateTermProtFee3,
        true: onMutateTermProtFee4
      }
    },
    ContinuousProtection: {
      false: {
        false: onMutateContProtFee1,
        true: onMutateContProtFee2
      },
      true: {
        false: onMutateContProtFee3,
        true: onMutateContProtFee4
      }
    }
  }

  const previousClientName = useRef(null)

  const onClientNameChange = (updatedValue: any) => {
    if (previousClientName.current === updatedValue) return
    onMutate(c => (c.clientName = updatedValue))
    previousClientName.current = updatedValue
  }

  return (
    <StyledNewClientBlock key="client_edit_form_options_container" mx={5} my={6}>
      <Space key="txt_lbl_client_name" mb={6}>
        <Label>Client name</Label>
        <TextInput
          key="txt_client_name"
          isError={isErrorWith(errors, 'clientName')}
          maxLength={64}
          value={clientName}
          onDebouncedValue={onClientNameChange}
        />
        <ValidationMessage errors={errors} fieldName="clientName" />
      </Space>
      <Space mb={6}>
        <Label>Strategies</Label>
        <StrategySelector
          strategies={strategyPermissions}
          onChange={(name, value) => {
            const mutation = (client: Client) => {
              client.disallowedStrategies = setStrategies(name, client.disallowedStrategies, value)
            }

            onMutate(mutation)
          }}
        />
      </Space>
      <Space mb={6}>
        <Label>Type of client</Label>
        <Space mb={3}>
          <RadioInput
            checked={client.clientType === 'Institutional'}
            id="institutional"
            type="radio"
            onChange={() => onMutate(x => (x.clientType = 'Institutional'))}
          />
          <Space isinlineblock ml={3}>
            <label htmlFor="institutional">Institutional</label>
          </Space>
        </Space>
        <Space>
          <RadioInput
            checked={client.clientType === 'Retail'}
            id="retail"
            type="radio"
            onChange={() =>
              onMutate(x => {
                x.clientType = 'Retail'
                x.reinvestmentType = 'ReinvestOnly'
              })
            }
          />
          <Space isinlineblock ml={3}>
            <label htmlFor="retail">Retail</label>
          </Space>
        </Space>
      </Space>
      <Space mb={6}>
        <Label>Default deposit instrument</Label>
        <Select
          value={client.defaultCashInstruments.depositId}
          onChange={v => onMutate(x => (x.defaultCashInstruments.depositId = v.target.value))}
        >
          <option value="">Select a deposit instrument</option>
          {availableDepositInstruments.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <ValidationMessage errors={errors} fieldName="depositId" />
      </Space>
      <Space mb={6}>
        <Label>Default loan instrument</Label>
        <Select
          value={client.defaultCashInstruments.loanId}
          onChange={v => onMutate(x => (x.defaultCashInstruments.loanId = v.target.value))}
        >
          <option value="">Select a loan instrument</option>
          {availableLoanInstruments.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <ValidationMessage errors={errors} fieldName="loanId" />
      </Space>
      <Space mb={6}>
        <Label>Default counterparty Id</Label>
        <Select
          value={client.tradeBuildingSettings.defaultNoteCounterpartyId}
          onChange={v =>
            onMutate(x => (x.tradeBuildingSettings.defaultNoteCounterpartyId = v.target.value))
          }
        >
          <option value="">Select a counterparty Id</option>
          {availableCptyIds.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <ValidationMessage errors={errors} fieldName="cptyId" />
      </Space>
      {existingClient && (
        <>
          <Space mb={6}>
            <Label>Note charges</Label>
            <Section pb={2} pt={4} px={1} type="primary">
              <FeesInput
                availableCptyIds={availableCptyIds}
                availableFeeBases={availableFeeBases}
                availableFeeFreqs={availableFeeFreqs}
                availableFeeTypes={filterFeeTypes(availableFeeTypes, 'note')}
                displayFees={client.defaultFees.defaultNoteFees}
                feeCategory="note"
                newFee={newNoteFee}
                title=""
                onChangeClient={onMutate}
                onChangeNewFee={onMutateNoteFee}
              />
            </Section>
          </Space>
          <Space mb={6}>
            <Label>Strategy charges</Label>
            <Section pb={2} pt={4} px={1} type="primary">
              {client.defaultFees.defaultStrategyFees.map(
                (criteria, i) =>
                  !client.disallowedStrategies.includes(criteria.strategyTemplateId) &&
                  strategyIds.includes(criteria.strategyTemplateId) && (
                    <FeesInput
                      key={`${criteria.strategyTemplateId}-${criteria.hasBankGuarantee}-${criteria.isLeveraged}`}
                      availableCptyIds={availableCptyIds}
                      availableFeeBases={availableFeeBases}
                      availableFeeFreqs={availableFeeFreqs}
                      availableFeeTypes={filterFeeTypes(
                        availableFeeTypes,
                        criteria.strategyTemplateId,
                        criteria.hasBankGuarantee
                      )}
                      bankGuarantee={unpascal(String(criteria.hasBankGuarantee))}
                      displayFees={criteria.fees}
                      feeCategory="strategy"
                      isLeveraged={unpascal(String(criteria.isLeveraged))}
                      newFee={
                        newFeeMap[criteria.strategyTemplateId][criteria.hasBankGuarantee][
                          criteria.isLeveraged
                        ]
                      }
                      strategyFeeIndex={i}
                      title={unpascal(
                        criteria.strategyTemplateId === 'Delta1'
                          ? 'Delta One'
                          : criteria.strategyTemplateId
                      )}
                      onChangeClient={onMutate}
                      onChangeNewFee={
                        mutationMap[criteria.strategyTemplateId][criteria.hasBankGuarantee][
                          criteria.isLeveraged
                        ]
                      }
                    />
                  )
              )}
            </Section>
          </Space>
        </>
      )}
      <Typography fontSize="13px" variant="b3">
        * Total trade value means Ref Portfolio Value Excl Leverage, Inc Clearing Provision
      </Typography>
      <ValidationMessage errors={errors} fieldName="feeRate" />
    </StyledNewClientBlock>
  )
}
